import { useEffect, useState } from 'react'

// Redux
import { useSelector, useDispatch, DefaultRootState } from 'react-redux'
import {
  monthRequest,
  reportRequest,
  singleStoreLastYearReportRequest
} from '../../store/modules/report/actions'
import { inputListRequest } from 'store/modules/input/actions'

// MaterialUI
import { makeStyles } from '@material-ui/core/styles'
import {
  Box,
  Typography,
  InputLabel,
  MenuItem,
  FormControl,
  ListItemText,
  Input,
  Checkbox,
  Select,
  MenuProps as MenuPropsType
} from '@material-ui/core'

import {
  CloseTwoTone,
  CloudDownloadTwoTone,
  TableChartTwoTone,
  DateRange
} from '@material-ui/icons'

// Compoments
import ReportsTable from './components/ReportsTable'
import Tooltip from '../../components/Tooltip'

// Libs
import { format, getMonth } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import moment from 'moment'
import * as XLSX from 'xlsx'

// Styles
import {
  CalendarContainer,
  ConversionChip,
  ReportCalendar,
  SheetButton,
  SheetModal
} from './components/styles'

// Utils
import { formatPrice, formatToPercentage, safeDivision } from 'utils/format'
import { getConversionExportValues } from 'utils/conversion'
import {
  getReportExportValue,
  isFieldDisabled
} from 'utils/reportDisabledFields'
import { getReportExportDates } from './reportExportDates'
import DayGoals from './components/DayGoals'
import { IInputProps } from 'store/modules/input/types'
import {
  IMonthReport,
  IReportProps,
  ISeller,
  IStore
} from 'store/modules/report/types'
import { IGoalProps } from 'store/modules/goal/types'
import { IStoreProps } from 'store/modules/store/types'

const useStyles = makeStyles(theme => ({
  root: {
    padding: theme.spacing(3)
  },
  content: {
    marginTop: theme.spacing(2)
  },
  subContent: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
    marginBottom: theme.spacing(2)
  },
  select: {
    width: '400px'
  }
}))

const MenuProps: Partial<MenuPropsType> = {
  PaperProps: {
    style: {
      maxHeight: 300,
      width: 250
    }
  },
  anchorOrigin: {
    vertical: 'bottom',
    horizontal: 'left'
  },
  transformOrigin: {
    vertical: 'top',
    horizontal: 'left'
  },
  getContentAnchorEl: null
}

interface SelectorInput {
  state: DefaultRootState
  input: IInputProps
}

interface SelectorReport {
  state: DefaultRootState
  report: IReportProps
}

interface SelectorGoal {
  state: DefaultRootState
  goal: IGoalProps
}

interface SelectorStore {
  state: DefaultRootState
  store: IStoreProps
}

interface RangeProps {
  from: Date
  to: Date
}

interface ReportsProps {
  match: {
    isExact: boolean
    params: {
      storeId: string
    }
    path: string
    url: string
  }
}

// Tabela para todas as lojas
const tableHeaders = [
  { id: 'name', label: 'Nome', minWidth: 150 },
  { id: 'sold', label: 'Faturamento', minWidth: 125 },
  { id: 'goalPercent', label: '%', minWidth: 75 },
  { id: 'goal', label: 'Meta ideal', minWidth: 125 },
  { id: 'mainGoalPercent', label: '%', minWidth: 75 },
  { id: 'mainGoal', label: 'Meta do mês ', minWidth: 125 },
  { id: 'currentGoal', label: 'Meta atual ', minWidth: 130 },
  { id: 'projection', label: 'Projeção de Venda', minWidth: 175 },
  { id: 'currentCommission', label: 'Remuneração Atual', minWidth: 175 },
  { id: 'commissionProj', label: 'Projeção de Remuneração', minWidth: 200 },
  { id: 'salesQuantity', label: 'N˚ Vendas', minWidth: 125 },
  { id: 'salesQuantityGoal', label: 'Meta Vendas', minWidth: 100 },
  { id: 'averageTicket', label: 'Ticket Médio', minWidth: 125 },
  { id: 'averageTicketGoal', label: 'Meta TM', minWidth: 100 },
  { id: 'items/sales', label: 'Peça/Venda', minWidth: 100 },
  { id: 'itemsSalesGoal', label: 'Meta PA', minWidth: 100 },
  { id: 'averagePrice', label: 'Preço Médio', minWidth: 125 },
  { id: 'averagePriceGoal', label: 'Meta PM', minWidth: 100 },
  { id: 'itemsQuantity', label: 'N˚ Peças', minWidth: 100 },
  { id: 'itemsQuantityGoal', label: 'Meta Peças', minWidth: 100 }
] as const

// Colunas da planilha que só existem para loja com contagem de pessoas
const CONVERSION_SHEET_HEADERS = ['Pessoas', 'Conversão']

export type TableHeadersProps = readonly (typeof tableHeaders)[number][]

const headersSheet = {
  Nome: 'General',
  CPF: 'General',
  Faturamento: '"R$ "#,##0.00',
  '%': '0.00%',
  'Meta ideal': '"R$ "#,##0.00',
  '%.': '0.00%',
  'Meta do mês': '"R$ "#,##0.00',
  'Meta Atual': 'General',
  'Projeção de Venda': '"R$ "#,##0.00',
  'Remuneração Atual': '"R$ "#,##0.00',
  'Projeção de Remuneração': '"R$ "#,##0.00',
  'N˚ Vendas': '0',
  'Meta Vendas': '0',
  'Ticket Médio': '"R$ "#,##0.00',
  'Meta TM': '"R$ "#,##0.00',
  'Peça/Venda': '0.00',
  'Meta PA': '0.00',
  'Preço Médio': '"R$ "#,##0.00',
  'Meta PM': '"R$ "#,##0.00',
  'N˚ Peças': '0',
  'Meta Peças': '0',
  'Piso Salarial': '"R$ "#,##0.00',
  DSR: '0',
  '% da Comissão': '0.00%',
  Bônus: ['"R$ "#,##0.00', '0.00%'],
  Pessoas: '0',
  Conversão: '0.00%'
} as const

export type HeadersSheetKeys = keyof typeof headersSheet

export default function Reports({ match }: ReportsProps) {
  const classes = useStyles()

  const dispatch = useDispatch()
  const {
    monthReport,
    reportsList,
    range,
    singleStoreLastYearReport
  } = useSelector<SelectorReport, IReportProps>(state => state.report)
  const { currentGoal } = useSelector<SelectorGoal, IGoalProps>(
    state => state.goal
  )
  const { store } = useSelector<SelectorStore, IStoreProps>(
    state => state.store
  )
  const { inputList, metadata } = useSelector<SelectorInput, IInputProps>(
    state => state.input
  )

  const { storeId } = match.params
  const token = window.localStorage.getItem('@NeoPro:token')

  const [reports, setReports] = useState<IMonthReport | null>(null)

  const [sheetModalVisible, setSheetModalVisible] = useState(false)
  const [openCalendar, setOpenCalendar] = useState(false)

  const [sheetColumnItems, setSheetColumnItems] = useState<HeadersSheetKeys[]>([
    'Nome',
    'CPF',
    'Faturamento',
    'Meta do mês'
  ])

  // Ao inicializar, se não tiver exibindo nenhum report, solicita os do mês
  useEffect(() => {
    if (metadata.pagination.totalCount > 0) {
      if (!monthReport && !reportsList && !reports) {
        dispatch(monthRequest(storeId, token))
      }
    } else {
      if (metadata.pagination.totalCount < 0) {
        dispatch(inputListRequest(storeId, token))
      }
    }
  }, [
    dispatch,
    storeId,
    token,
    metadata.pagination.totalCount,
    monthReport,
    reportsList,
    reports
  ])

  useEffect(() => {
    if (storeId && range.from) {
      const month = getMonth(new Date(range.from)) + 1
      dispatch(
        singleStoreLastYearReportRequest(storeId, month, true, token)
      )
    }
  }, [dispatch, storeId, range.from, token])

  // Se chegar os reports do mês, e não tiver nenhum intervalo pré selecionado, atualiza
  useEffect(() => {
    // apresenta reports do intervalo selecionado
    if (reportsList) setReports(reportsList)
    else setReports(monthReport)
  }, [monthReport, reportsList])

  useEffect(() => {
    if (
      metadata.pagination.totalCount > 0 &&
      !monthReport &&
      !reportsList &&
      !reports
    ) {
      dispatch(monthRequest(storeId, token))
    }
  }, [metadata])

  // Tabela atualmente destinada apenas as lojas que possuem OtherInputs
  const tableOtherHeaders = [
    { id: 'name', label: 'Nome', minWidth: 150 },
    { id: 'sold', label: 'Faturamento', minWidth: 125 },
    { id: 'goalPercent', label: '%', minWidth: 75 },
    { id: 'goal', label: 'Meta ideal', minWidth: 100 },
    { id: 'mainGoalPercent', label: '%', minWidth: 75 },
    { id: 'mainGoal', label: 'Meta do mês', minWidth: 125 },
    { id: 'currentGoal', label: 'Meta atual', minWidth: 130 },
    { id: 'projection', label: 'Projeção de Venda', minWidth: 175 },
    { id: 'salesQuantity', label: 'N˚ Vendas', minWidth: 125 },
    { id: 'salesQuantityGoal', label: 'Meta Vendas', minWidth: 100 },
    { id: 'averageTicket', label: 'Ticket Médio', minWidth: 125 },
    { id: 'averageTicketGoal', label: 'Meta TM', minWidth: 100 },
    { id: 'items/sales', label: 'Peça/Venda', minWidth: 100 },
    { id: 'itemsSalesGoal', label: 'Meta PA', minWidth: 100 },
    { id: 'averagePrice', label: 'Preço Médio', minWidth: 125 },
    { id: 'averagePriceGoal', label: 'Meta PM', minWidth: 100 },
    { id: 'itemsQuantity', label: 'N˚ Peças', minWidth: 100 },
    { id: 'itemsQuantityGoal', label: 'Meta Peças', minWidth: 100 }
  ]

  const hasConversion = Boolean(reports?.store?.conversion)
  const sheetHeaderOptions = (Object.keys(headersSheet) as HeadersSheetKeys[])
    .filter(header => hasConversion || !CONVERSION_SHEET_HEADERS.includes(header))

  const handleOnExport = () => {
    if (reports && store) {
      const exportDates = getReportExportDates(range)
      const disabledFields = reports.disabledFields
      const acDayGoalReason = disabledFields.metric?.acDayGoal
      const mainGoalReason = disabledFields.metric?.mainGoal
      const goalValueReason = disabledFields.metric?.goalValue
      const projectionGoalReason = disabledFields.projection?.goal
      const projectionSoldReason = disabledFields.projection?.sold
      const commissionReason = disabledFields.commission
      const projectionCommissionReason = disabledFields.projection?.commission
      const dynamicGoalSalesReason = disabledFields.dynamicGoal?.sales
      const dynamicGoalItemsReason = disabledFields.dynamicGoal?.items
      const salesDataReason = disabledFields.total?.sold ||
        disabledFields.total?.sales || disabledFields.total?.items
      const salesDataDisabled = isFieldDisabled(salesDataReason)
      const formatStoreDataTable = (storeToBeFormatted: IStore) => [
        'Loja',
        '', // CPF dos vendedores
        salesDataDisabled ? '-' : storeToBeFormatted.total.sold ?? 0,
        salesDataDisabled || isFieldDisabled(acDayGoalReason)
          ? '-'
          : safeDivision(
            storeToBeFormatted.total.sold,
            storeToBeFormatted.metric.acDayGoal
          ), // Porcentagem da meta ideal
        getReportExportValue(
          acDayGoalReason,
          storeToBeFormatted.metric.acDayGoal ?? 0
        ), // Meta ideal
        salesDataDisabled || isFieldDisabled(mainGoalReason)
          ? '-'
          : safeDivision(
            storeToBeFormatted.total.sold,
            storeToBeFormatted.metric.mainGoal
          ), // Porcentagem da meta do mês
        getReportExportValue(
          mainGoalReason,
          storeToBeFormatted.metric.mainGoal ?? 0
        ), // Meta do mês
        '-', // Meta Atual
        getReportExportValue(
          projectionSoldReason,
          storeToBeFormatted.projection.sold ?? 0
        ), // Projeção de Venda
        '-', // Remuneração Atual
        '-', // Projeção de Remuneração
        salesDataDisabled ? '-' : storeToBeFormatted.total.sales ?? 0,
        getReportExportValue(
          dynamicGoalSalesReason,
          Math.ceil(storeToBeFormatted.dynamicGoal.sales)
        ), // Meta Vendas
        salesDataDisabled
          ? '-'
          : safeDivision(
            storeToBeFormatted.total.sold,
            storeToBeFormatted.total.sales
          ), // Ticket Médio
         storeToBeFormatted.dynamicGoal.ticket, // Meta TM
        salesDataDisabled
          ? '-'
          : safeDivision(
            storeToBeFormatted.total.items,
            storeToBeFormatted.total.sales
          ), // Peça/Venda
         storeToBeFormatted.dynamicGoal.itemsSale, // Meta PA
        salesDataDisabled
          ? '-'
          : safeDivision(
            storeToBeFormatted.total.sold,
            storeToBeFormatted.total.items
          ), // Preço Médio
        storeToBeFormatted.dynamicGoal.price, // Meta PM
        salesDataDisabled ? '-' : storeToBeFormatted.total.items ?? 0,
        getReportExportValue(
          dynamicGoalItemsReason,
          Math.ceil(storeToBeFormatted.dynamicGoal.items)
        ), // Meta Peças
        getReportExportValue(
          commissionReason,
          currentGoal?.salary.base ?? 0
        ), // Piso Salarial
        getReportExportValue(commissionReason, currentGoal?.dsr), // DSR
        '-', // % da Comissão
        '-', // Bônus
        // Pessoas / Conversão
        ...getConversionExportValues(
          reports.store.conversion && {
            sales: salesDataDisabled ? '-' : storeToBeFormatted.total.sales,
            visitors: reports.store.conversion.total.visitors
          },
          salesDataReason
        )
      ]

      const sheetIndex = sheetColumnItems.map(item =>
        (Object.keys(headersSheet) as HeadersSheetKeys[]).findIndex(
          header => header === item
        )
      )

      // Informações gerais da Loja
      const storeOptions = formatStoreDataTable(reports?.store)

      const storeItems = sheetColumnItems.map(
        item => storeOptions[sheetIndex[sheetColumnItems.indexOf(item)]]
      )

      const formatSellersTableData = (
        sellerToBeFormatted: ISeller,
        isOscar = false
      ) => [
        sellerToBeFormatted.active
          ? sellerToBeFormatted.name.complete
          : `(INATIVO) ${sellerToBeFormatted.name.complete}`, // Nome do vendedor
        sellerToBeFormatted.cpf, // cpf do vendedor
        salesDataDisabled ? '-' : sellerToBeFormatted.total.sold ?? 0,
        salesDataDisabled || isFieldDisabled(acDayGoalReason)
          ? '-'
          : safeDivision(
            sellerToBeFormatted.total.sold,
            sellerToBeFormatted.metric.acDayGoal
          ), // Porcentagem da Meta ideal do vendedor
        getReportExportValue(
          acDayGoalReason,
          sellerToBeFormatted.metric.acDayGoal ?? 0
        ), // Meta ideal do vendedor
        salesDataDisabled || isFieldDisabled(goalValueReason)
          ? '-'
          : safeDivision(
            sellerToBeFormatted.total.sold,
            sellerToBeFormatted.metric.goalValue
          ), // Porcentagem da Meta do mês do vendedor
        getReportExportValue(
          goalValueReason,
          sellerToBeFormatted.metric.goalValue ?? 0
        ), // Meta do mes do vendedor
        !isOscar
          ? getReportExportValue(
            projectionGoalReason,
            sellerToBeFormatted.projection.goal.name
          )
          : '-', // Meta Atual
        getReportExportValue(
          projectionSoldReason,
          sellerToBeFormatted.projection.sold ?? 0
        ), // Projeção de Venda
          getReportExportValue(
            commissionReason,
            sellerToBeFormatted.commission.sumSectionsCommissions ??
              sellerToBeFormatted.commission.sumCommission ??
              0
        ), // Remuneração Atual
        !isOscar
          ? getReportExportValue(
            projectionCommissionReason,
            sellerToBeFormatted.projection.commission
              .sumSectionsCommissions ??
              sellerToBeFormatted.projection.commission.sumCommission ??
              0
          )
          : '-', // Projeção de Remuneração
        salesDataDisabled ? '-' : sellerToBeFormatted.total.sales ?? 0,
        getReportExportValue(
          dynamicGoalSalesReason,
          Math.ceil(sellerToBeFormatted.dynamicGoal.sales)
        ), // Meta Vendas
        salesDataDisabled
          ? '-'
          : safeDivision(
            sellerToBeFormatted.total.sold,
            sellerToBeFormatted.total.sales
          ), // Ticket Médio
         sellerToBeFormatted.dynamicGoal.ticket, // Meta TM
        salesDataDisabled
          ? '-'
          : safeDivision(
            sellerToBeFormatted.total.items,
            sellerToBeFormatted.total.sales
          ), // Peça/Venda
         sellerToBeFormatted.dynamicGoal.itemsSale, // Meta PA
        salesDataDisabled
          ? '-'
          : safeDivision(
            sellerToBeFormatted.total.sold,
            sellerToBeFormatted.total.items
          ), // Preço Médio
         sellerToBeFormatted.dynamicGoal.price, // Meta PM
        salesDataDisabled ? '-' : sellerToBeFormatted.total.items ?? 0,
        getReportExportValue(
          dynamicGoalItemsReason,
          Math.ceil(sellerToBeFormatted.dynamicGoal.items)
        ), // Meta Peças
        '-', // Piso Salarial
        '-', // DSR

        isFieldDisabled(commissionReason) ||
        isFieldDisabled(projectionGoalReason)
          ? '-'
          : currentGoal?.monthGoals.find(
            goal => goal.name === sellerToBeFormatted.projection.goal.name
          )?.commission ||
            (currentGoal?.monthGoals[0]?.commission ?? 0), // % da Comissão
        isFieldDisabled(commissionReason) ||
        isFieldDisabled(projectionGoalReason)
          ? '-'
          : currentGoal?.monthGoals.find(
            goal => goal.name === sellerToBeFormatted.projection.goal.name
          )?.bonus ||
            (currentGoal?.monthGoals[0]?.bonus ?? 0), // Bônus
        '-', // Pessoas
        '-' // Conversão
      ]

      let oscarSheetColumnValue: (string | number | undefined)[][] = []

      if (reports.otherInputs) {
        const total = salesDataDisabled
          ? { items: 0, sales: 0, sold: 0 }
          : reports.otherInputs.store.total

        const oscarStoreOptions = formatStoreDataTable({
          ...reports.store,
          dynamicGoal: reports.otherInputs.store.dynamicGoal,
          projection: { sold: reports.otherInputs.store.soldProjection },
          total
        })

        const oscarStoreItems = sheetColumnItems.map(
          item => oscarStoreOptions[sheetIndex[sheetColumnItems.indexOf(item)]]
        )
        oscarSheetColumnValue = [sheetColumnItems, oscarStoreItems]
      }

      let sheetColumnValue = [sheetColumnItems, storeItems]

      let sellersOtherInputCount = 0
      const sellersIndex: number[] = []
      reports.sellers.forEach((seller, index) => {
        const options = formatSellersTableData(seller)

        const items = sheetIndex.map(itemIndex => options[itemIndex])

        sheetColumnValue = [...sheetColumnValue, items]

        if (reports.otherInputs && seller.otherInputs) {
          const oscarOptions = formatSellersTableData(
            {
              ...seller,
              total: { ...seller.otherInputs }
            },
            true
          )

          const oscarItems = sheetIndex.map(
            itemIndex => oscarOptions[itemIndex]
          )
          oscarSheetColumnValue = [...oscarSheetColumnValue, oscarItems]
          sellersOtherInputCount++
          sellersIndex.push(index)
        }
      })

      const wb = XLSX.utils.book_new()

      const defaultColumnsExcel = [
        [store.name],
        [
          `${exportDates.from} até ${exportDates.to}`
        ],
        ...sheetColumnValue
      ]

      if (reports.otherInputs) {
        defaultColumnsExcel.splice(2, 0, [
          'Desempenho vendedores - Todos os canais'
        ])
        defaultColumnsExcel.splice(0, 1),
          defaultColumnsExcel.push(
            [],
            [`Desempenho Loja - ${store.name}`],
            ...oscarSheetColumnValue
          )
      }

      const ws = XLSX.utils.aoa_to_sheet(defaultColumnsExcel)
      const getSheetCellFormat = (
        item: HeadersSheetKeys,
        seller?: ISeller
      ): string => {
        const format = headersSheet[item]

        if (typeof format === 'string') return format
        if (
          isFieldDisabled(commissionReason) ||
          isFieldDisabled(projectionGoalReason)
        ) return 'General'

        const usesPercentage = currentGoal?.monthGoals.find(
          goal => goal.name === seller?.projection.goal.name
        )?.bonusPercentage ||
          (currentGoal?.monthGoals[0]?.bonusPercentage ?? false)

        return format[Number(usesPercentage)]
      }

      new Array(reports.sellers.length + 1).fill(0).forEach((_, index) => {
        new Array(sheetColumnItems.length).fill(0).forEach((_, idx) => {
          ws[`${XLSX.utils.encode_col(idx)}${index + 4}`].z =
            getSheetCellFormat(
              sheetColumnItems[idx],
              reports.sellers[index]
            )
        })
      })

      if (reports.otherInputs) {
        new Array(sellersOtherInputCount + 1).fill(0).forEach((_, index) => {
          new Array(sheetColumnItems.length).fill(0).forEach((_, idx) => {
            ws[
              `${XLSX.utils.encode_col(idx)}${
                index + 8 + reports.sellers.length
              }`
            ].z = getSheetCellFormat(
              sheetColumnItems[idx],
              reports.sellers[sellersIndex[index]]
            )
          })
        })
      }

      XLSX.utils.book_append_sheet(wb, ws, `Relatório`)
      XLSX.writeFile(
        wb,
        `NEOPRO_${format(new Date(reports.store.metric.today), 'dd-MM-yyyy', {
          locale: ptBR
        })}-Relatorio-${store?.name}-${exportDates.from}-a-${exportDates.to}.xlsx`,
        { bookType: 'xlsx', type: 'binary' }
      )
    }
  }

  // Só aparece para loja com integração de contagem de pessoas.
  const conversion = reports?.store?.conversion
  const conversionVisitors = (conversion?.total.visitors || 0).toLocaleString(
    'pt-BR'
  )
  const conversionSales = (reports?.store?.total.sales || 0).toLocaleString(
    'pt-BR'
  )
  const conversionRate = conversion?.total.visitors
    ? `${(
        ((reports?.store?.total.sales || 0) / conversion.total.visitors) *
        100
      )
        .toFixed(1)
        .replace('.', ',')}%`
    : '—'

  return (
    <div className={classes.root}>
      <div className={classes.content}>
        <div className={classes.subContent}>
          {inputList?.length === 0 && (
            <Tooltip
              id={`storeDoesNotHaveReport`}
              content={
                <p>A loja ainda não possui metas ou lançamentos cadastrados.</p>
              }
              place='top'
            />
          )}
          <CalendarContainer data-tip data-for='storeDoesNotHaveReport'>
            <Typography style={{ whiteSpace: 'nowrap' }}>
              Relatório para:
            </Typography>
            <ReportCalendar
              onClick={() => {
                setOpenCalendar(true)
              }}
              hasReport={inputList?.length > 0}
            >
              <DateRange style={{ width: 16, marginRight: 5 }} />
              <Typography>
                {range.from ? moment(range.from).format('DD/MM/YYYY') : '-'}
              </Typography>
              <Typography>até</Typography>
              <Typography>
                {range.to ? moment(range.to).format('DD/MM/YYYY') : '-'}
              </Typography>
            </ReportCalendar>
          </CalendarContainer>

          {conversion && (
            <>
              <ConversionChip>
                <div className='icon'>
                  <svg
                    viewBox='0 0 24 24'
                    fill='none'
                    stroke='currentColor'
                    strokeWidth={2}
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    aria-hidden='true'
                  >
                    <path d='M4 20V5a1 1 0 0 1 1-1h8a1 1 0 0 1 1 1v15' />
                    <path d='M3 20h14' />
                    <path d='M10.5 12h.01' />
                    <path d='M17 9l3 3-3 3M14 12h6' />
                  </svg>
                </div>
                <span className='label'>CONVERSÃO</span>
                <span className='rate'>{conversionRate}</span>
                <span className='divider' />
                <span className='detail'>
                  <strong>{conversionSales}</strong> vendas de{' '}
                  <strong>{conversionVisitors}</strong> pessoas
                </span>
                {/* O data-tip vai no span: um <svg> sem preenchimento só
                    recebe o mouse em cima do traço, e o hover no meio do
                    ícone não dispararia o tooltip. */}
                <span
                  className='info'
                  data-tip
                  data-for='conversionExplanation'
                >
                  <svg
                    viewBox='0 0 24 24'
                    fill='none'
                    stroke='currentColor'
                    strokeWidth={2}
                    strokeLinecap='round'
                    aria-label='Vendas efetivas dividido por pessoas que entraram na loja no período'
                  >
                    <circle cx='12' cy='12' r='9' />
                    <path d='M12 11v5M12 8h.01' />
                  </svg>
                </span>
              </ConversionChip>
              <Tooltip
                id='conversionExplanation'
                place='bottom'
                content={
                  <p>
                    Vendas efetivas ÷ pessoas que entraram na loja no período.
                    As pessoas são contadas por câmera na entrada; as vendas vêm
                    dos lançamentos.
                  </p>
                }
              />
            </>
          )}

          <SheetButton onClick={() => setSheetModalVisible(true)}>
            <TableChartTwoTone style={{ marginRight: 5 }} />
            Exportar relatório para planilhas
          </SheetButton>
        </div>

        {store && (
          <ReportsTable
            reports={reports}
            store={store}
            tableHeaders={tableHeaders}
            tableOtherHeaders={tableOtherHeaders}
            range={range}
            lastYearVariation={singleStoreLastYearReport?.variation?.sold}
            requestReports={(range: RangeProps) => {
              dispatch(reportRequest(storeId, token, range))
            }}
            openCalendar={openCalendar}
            setOpenCalendar={setOpenCalendar}
          />
        )}
        <DayGoals
          style={{
            marginTop: '24px'
          }}
          data={reports}
          range={range}
        />
      </div>

      {reports && (
        <SheetModal
          open={sheetModalVisible}
          onClose={() => setSheetModalVisible(false)}
        >
          <div className='content'>
            <h1>O que deseja exportar?</h1>
            <span>Selecione abaixo as informações que deseja exportar</span>

            <div
              className='close-button'
              onClick={() => setSheetModalVisible(false)}
            >
              <CloseTwoTone style={{ color: 'lightgrey' }} />
            </div>

            <div className='checkbox-container'>
              <FormControl>
                <InputLabel id='export-label'>Informações</InputLabel>
                <Select
                  multiple
                  labelId='export-label'
                  id='export-label-checkbox'
                  value={Object.values(sheetColumnItems)}
                  onChange={event =>
                    setSheetColumnItems(
                      event.target.value as HeadersSheetKeys[]
                    )
                  }
                  input={<Input />}
                  renderValue={selected => {
                    const typedSelected = selected as string[]
                    return typedSelected.join(', ')
                  }}
                  MenuProps={MenuProps}
                  className={classes.select}
                >
                  {sheetHeaderOptions.map(
                    header => {
                      const isInteractionDisabled = [
                        'Nome',
                        'Faturamento'
                      ].includes(header)

                      return (
                        <MenuItem
                          key={header}
                          value={header}
                          disabled={isInteractionDisabled}
                        >
                          <Checkbox
                            checked={
                              isInteractionDisabled ||
                              Object.values(sheetColumnItems).indexOf(header) >
                                -1
                            }
                            disabled={isInteractionDisabled}
                            style={{
                              color: '#1E2FA9'
                            }}
                          />
                          <ListItemText primary={header} />
                        </MenuItem>
                      )
                    }
                  )}
                </Select>
              </FormControl>
            </div>

            <SheetButton onClick={handleOnExport}>
              <CloudDownloadTwoTone style={{ marginRight: 5 }} />
              Fazer download da planilha
            </SheetButton>
          </div>
        </SheetModal>
      )}
    </div>
  )
}
